import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { fmtAED } from '../lib/format';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, Users, LayoutGrid, ArrowRightLeft, Receipt, CalendarClock, ShoppingCart, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import PageHelp from '../components/PageHelp';

const STATUS_COLORS = {
  available: 'bg-st-available', occupied: 'bg-st-occupied', reserved: 'bg-st-reserved',
  billed: 'bg-st-billed', needs_cleaning: 'bg-st-cleaning',
};
const STATUS_LABEL = { available: 'Available', occupied: 'Occupied', reserved: 'Reserved', billed: 'Billed', needs_cleaning: 'Cleaning' };

export default function Tables() {
  const { currentOutlet, can } = useAuth();
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: '', section: 'AC Hall', seats: 4 });
  const [selected, setSelected] = useState(null);
  const [checks, setChecks] = useState([]);
  const [transferFor, setTransferFor] = useState(null);
  const dragRef = useRef(null);

  const load = async () => {
    if (!currentOutlet) return;
    const { data } = await api.get('/tables', { params: { outlet_id: currentOutlet.id } });
    setTables(data);
    return data;
  };
  useEffect(() => { load(); }, [currentOutlet]);

  const openTable = async (t) => {
    setSelected(t); setChecks([]);
    try { const { data } = await api.get(`/tables/${t.id}/checks`); setChecks(data); } catch (e) {}
  };

  const addTable = async () => {
    await api.post('/tables', { ...form, outlet_id: currentOutlet.id, x: 80, y: 80 });
    setAddOpen(false); setForm({ name: '', section: 'AC Hall', seats: 4 }); load();
    toast.success('Table added');
  };

  const autoArrange = async () => {
    const { data } = await api.post('/tables/auto-arrange', null, { params: { outlet_id: currentOutlet.id } });
    setTables(data); toast.success('Tables auto-arranged');
  };

  const setStatus = async (t, status) => {
    await api.patch(`/tables/${t.id}/status`, null, { params: { status } });
    setSelected(null); load();
  };

  const deleteTable = async (t) => {
    if (!window.confirm(`Delete ${t.name}?`)) return;
    await api.delete(`/tables/${t.id}`); setSelected(null); load(); toast.success('Table deleted');
  };

  const onDragEnd = async (t, e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const x = Math.max(0, e.clientX - rect.left - 55);
    const y = Math.max(0, e.clientY - rect.top - 40);
    await api.patch(`/tables/${t.id}/position`, { x, y });
    load();
  };

  const doTransfer = async (destId) => {
    try {
      await api.post(`/orders/${transferFor.id}/transfer`, { table_id: destId });
      toast.success('Order transferred'); setTransferFor(null); setSelected(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Transfer failed'); }
  };

  const counts = tables.reduce((a, t) => { a[t.status] = (a[t.status] || 0) + 1; return a; }, {});

  return (
    <div className="p-4 md:p-6 space-y-4">
      <PageHelp id="tables">Drag tables to arrange your floor, or hit <b>Auto Arrange</b>. Tap a table to see its <b>checks</b> (each customer = a separate check), <b>open/add an order</b>, <b>reserve</b>, <b>transfer</b> a check to another table, or <b>settle</b> the bill.</PageHelp>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-2xl font-bold">Floor Plan</h1>
          <p className="text-sm text-muted-foreground">{currentOutlet?.name} · {tables.length} tables</p>
        </div>
        {can('tables') && (
          <div className="flex gap-2">
            <Button variant="outline" data-testid="auto-arrange-button" onClick={autoArrange}><LayoutGrid className="h-4 w-4 mr-2" /> Auto Arrange</Button>
            <Button data-testid="add-table-button" onClick={() => setAddOpen(true)}><Plus className="h-4 w-4 mr-2" /> Add Table</Button>
          </div>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        {Object.keys(STATUS_LABEL).map((s) => (
          <div key={s} className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-card border">
            <span className={`h-2.5 w-2.5 rounded-full ${STATUS_COLORS[s]}`} /> {STATUS_LABEL[s]} <Badge variant="secondary">{counts[s] || 0}</Badge>
          </div>
        ))}
      </div>

      <Card className="relative overflow-hidden" style={{ minHeight: 520 }} ref={dragRef} data-testid="tables-floor-canvas">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(hsl(var(--border)) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        {tables.map((t) => (
          <div key={t.id} data-testid={`table-node-${t.name}`} draggable
            onDragEnd={(e) => onDragEnd(t, e, dragRef.current)}
            onClick={() => openTable(t)}
            style={{ left: t.x, top: t.y }}
            className="absolute cursor-move select-none">
            <div className="rounded-2xl border-2 px-4 py-3 w-28 text-center shadow-sm bg-card hover:shadow-md transition-shadow" style={{ borderColor: `hsl(var(--st-${t.status === 'needs_cleaning' ? 'cleaning' : t.status}))` }}>
              <div className={`h-2 w-full rounded-full mb-2 ${STATUS_COLORS[t.status]}`} />
              <div className="font-display font-bold">{t.name}</div>
              <div className="text-[10px] text-muted-foreground flex items-center justify-center gap-1"><Users className="h-3 w-3" />{t.seats} · {t.section}</div>
            </div>
          </div>
        ))}
        {tables.length === 0 && <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">No tables yet. Add one to start.</div>}
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Table</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="table-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="T13" /></div>
            <div><Label>Section</Label>
              <select className="w-full h-9 rounded-md border bg-background px-2" value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })}>
                {['AC Hall', 'Terrace', 'Bar', 'Non-AC'].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div><Label>Seats</Label><Input type="number" value={form.seats} onChange={(e) => setForm({ ...form, seats: Number(e.target.value) })} /></div>
            <Button data-testid="table-save-button" className="w-full" onClick={addTable}>Add</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Table actions + checks */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2">{selected?.name}
            <Badge variant="secondary" className="capitalize">{STATUS_LABEL[selected?.status] || selected?.status}</Badge></DialogTitle></DialogHeader>

          {/* Running checks */}
          {checks.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-semibold text-muted-foreground">RUNNING CHECKS ({checks.length})</div>
              {checks.map((c, i) => (
                <div key={c.id} className="rounded-lg border p-2.5 space-y-2" data-testid={`table-check-${i + 1}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">Check {i + 1} <span className="text-muted-foreground font-normal">· {c.order_no}</span></div>
                    <div className="font-mono text-sm">{fmtAED(c.grand_total)}</div>
                  </div>
                  <div className="text-[11px] text-muted-foreground">{(c.items || []).length} item(s) · {c.status}</div>
                  <div className="grid grid-cols-3 gap-1.5">
                    <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`check-open-${i + 1}`} onClick={() => navigate(`/pos?order=${c.id}`)}>Open</Button>
                    <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`check-transfer-${i + 1}`} onClick={() => setTransferFor(c)}><ArrowRightLeft className="h-3 w-3 mr-1" />Move</Button>
                    <Button size="sm" className="h-8 text-xs" data-testid={`check-settle-${i + 1}`} onClick={() => navigate(`/pos?order=${c.id}&pay=1`)}><Receipt className="h-3 w-3 mr-1" />Settle</Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Primary actions */}
          <div className="space-y-2 pt-1">
            <Button className="w-full" data-testid="table-open-order" onClick={() => navigate(`/pos?table=${selected.id}&new=1`)}>
              <ShoppingCart className="h-4 w-4 mr-2" /> {checks.length > 0 ? 'Add Another Check' : 'Open Order'}
            </Button>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" data-testid="table-reserve" onClick={() => setStatus(selected, 'reserved')}><CalendarClock className="h-4 w-4 mr-1" /> Reserve</Button>
              <Button variant="outline" data-testid="table-available" onClick={() => setStatus(selected, 'available')}>Mark Available</Button>
              <Button variant="outline" data-testid="table-cleaning" onClick={() => setStatus(selected, 'needs_cleaning')}>Cleaning</Button>
              {can('tables') && checks.length === 0 && <Button variant="outline" className="text-destructive" data-testid="table-delete" onClick={() => deleteTable(selected)}><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer target picker */}
      <Dialog open={!!transferFor} onOpenChange={(o) => !o && setTransferFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Transfer {transferFor?.order_no} → which table?</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-2 max-h-72 overflow-y-auto">
            {tables.filter((t) => t.id !== selected?.id).map((t) => (
              <button key={t.id} data-testid={`transfer-target-${t.name}`} onClick={() => doTransfer(t.id)}
                className="rounded-lg border p-2 text-center hover:border-primary hover:bg-muted transition-colors">
                <div className="font-medium text-sm">{t.name}</div>
                <div className="text-[10px] text-muted-foreground capitalize">{STATUS_LABEL[t.status]}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
